/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import moment from "moment";
import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import * as UserProfilesActions from "Dataflows/RD/UserProfile/RDUserProfileAction";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import {
  detailListStyles,
  spinnerStyles,
  StyledSpan,
} from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { isUserProfilesLoading } from "Dataflows/RD/UserProfile/RDUserProfileSelector";
import { formatDate } from "Utility/FormatHelper";

import { IRegionalDirectorsListItem } from "./IRegionalDirectorsListItem";
import { IRegionalDirectorsListProps } from "./IRegionalDirectorsListProps";

const formatStatusColumn = (statusKey: string): React.ReactElement => {
  return <StatusElement statusValue={statusKey} />;
};

const formatDisplayDate = (incomingDate: Date | undefined): string =>
  !!incomingDate ? formatDate(incomingDate) : "";

/**
 * List of regional director profiles
 * @Component
 * @returns {React.FunctionComponent<IRegionalDirectorsListProps>}
 */
const RegionalDirectorsLists: React.FunctionComponent<IRegionalDirectorsListProps> = (
  props: IRegionalDirectorsListProps
) => {
  const history = useHistory();
  const { data, pageSize, filteredCount, currentPage } = props;
  const profilesToRender = data ? data.slice(0, pageSize) : [];
  const dispatch = useDispatch();
  const isLoadingUsers = useSelector(isUserProfilesLoading);

  const [columns, setColumns] = useState<IColumn[]>([
    {
      key: "id",
      name: "ID",
      ariaLabel: "ID",
      fieldName: "id",
      minWidth: 20,
      maxWidth: 36,
      isResizable: true,
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "First Name",
      fieldName: "firstName",
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Last name",
      fieldName: "lastName",
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "primaryEmail",
      name: "Primary Email",
      ariaLabel: "Primary Email",
      fieldName: "primaryEmail",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: "cpm",
      name: "CPM",
      ariaLabel: "CPM",
      fieldName: "cpm",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: "awardPeriodStart",
      name: "Award Period Start",
      ariaLabel: "Award Period Start",
      fieldName: "awardPeriodStartDate",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: IRegionalDirectorsListItem): string =>
        formatDisplayDate(item.awardPeriodStartDate),
    },
    {
      key: "awardPeriodEndDate",
      name: "Award Period End",
      ariaLabel: "Award Period End",
      fieldName: "awardPeriodEndDate",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: IRegionalDirectorsListItem): string =>
        formatDisplayDate(item.awardPeriodEndDate),
    },
    {
      key: "applicationProfileStatus",
      name: "Application Status",
      ariaLabel: "Application status",
      fieldName: "applicationProfileStatus",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: IRegionalDirectorsListItem): React.ReactElement =>
        formatStatusColumn(item.applicationProfileStatus),
    },
    {
      key: "programStatus",
      name: "Program Status",
      ariaLabel: "Program status",
      fieldName: "programProfileStatus",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: IRegionalDirectorsListItem): React.ReactElement =>
        formatStatusColumn(item.programProfileStatus),
    },
  ]);

  const onSortingUpdate = (sortColumn?: string, sortDescending?: boolean): void => {
    dispatch(UserProfilesActions.updateRDSorting(sortColumn, sortDescending));
  };

  const onClickProfile = (profileId: number): void => {
    history.push(`/rd/details/${profileId}`);
  };

  const onRenderColumn = (
    item: IRegionalDirectorsListItem,
    index?: number,
    column?: IColumn
  ): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IRegionalDirectorsListItem];
    if (column?.key == "id") {
      return <StyledSpan>{fieldContent}</StyledSpan>;
    }
    return <span>{fieldContent}</span>;
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const profileId = (props?.item as IRegionalDirectorsListItem).id;
    return defaultRender({
      ...props,
      onClick: () => onClickProfile(profileId),
    });
  };

  const onColumnClickHandler = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    columns.forEach((col: IColumn) => {
      if (col.key == column.key) {
        const isSortedDescending = !col.isSortedDescending;
        col.isSortedDescending = isSortedDescending;
        col.isSorted = true;
        onSortingUpdate(col.fieldName, isSortedDescending);
      } else {
        col.isSorted = false;
        col.isSortedDescending = true;
      }
    });
  };

  useEffect(() => {
    const newColumns: IColumn[] = [];
    const sortingColumns = [
      "firstName",
      "lastName",
      "cpm",
      "awardPeriodStartDate",
      "awardPeriodEndDate",
    ];
    columns.forEach((col) => {
      if (sortingColumns.filter((c) => c === col.fieldName).length > 0) {
        col.onColumnClick = onColumnClickHandler;
      }
      newColumns.push(col);
    });
    setColumns(newColumns);
  }, []);

  if (isLoadingUsers) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  return (
    <>
      <DetailsList
        items={profilesToRender}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: any): string => `${(item as IRegionalDirectorsListItem).id}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        styles={detailListStyles}
        onRenderRow={onRenderRow}
        onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => dispatch(UserProfilesActions.updateRDCurrentPage(page))}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            dispatch(UserProfilesActions.updateRDPageSize(op?.key as number));
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default RegionalDirectorsLists;
